import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout'
import HomePage from './pages/HomePage'
import NotFoundPage from './pages/NotFoundPage'
import AuthSocialCompletePage from './pages/AuthSocialCompletePage'
import AuthPage from './pages/AuthPage'
import DashboardPage from './pages/DashboardPage'
import PublicWishlistPage from './pages/PublicWishlistPage'
import WishlistItemPage from './pages/WishlistItemPage'
import OnboardingPage from './pages/OnboardingPage'
import CookiePolicyPage from './pages/CookiePolicyPage'
import AboutPage from './pages/AboutPage'
import CommunityPage from './pages/CommunityPage'
import EmailConfirmedPage from './pages/EmailConfirmedPage'
import ProfilePage from './pages/ProfilePage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'

// =============================================================================
// CODERBLOCK NAVIGATION TRACKING
// Auto-initialize navigation tracking for Coderblock editor integration.
// (Patches the History API + relays the current path to the editor iframe.)
// =============================================================================
if (typeof window !== 'undefined' && window.parent !== window) {
  const sendNavigationUpdate = () => {
    const path = window.location.pathname + window.location.search + window.location.hash
    window.parent.postMessage({
      type: 'coderblock-navigation',
      mode: 'live',
      path: path || '/'
    }, '*')
  }

  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState

  history.pushState = function(...args: Parameters<typeof history.pushState>) {
    originalPushState.apply(history, args)
    setTimeout(sendNavigationUpdate, 0)
  }

  history.replaceState = function(...args: Parameters<typeof history.replaceState>) {
    originalReplaceState.apply(history, args)
    setTimeout(sendNavigationUpdate, 0)
  }

  window.addEventListener('popstate', sendNavigationUpdate)
  sendNavigationUpdate()

  // Listen for navigation commands from Coderblock editor (avoids iframe reload flash)
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'coderblock-navigate' && event.data.path) {
      const target = event.data.path
      if (window.location.pathname !== target) {
        history.pushState(null, '', target)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }
  })
}

// =============================================================================
// 🧭 EXPLICIT ROUTES
// =============================================================================
// Every page is registered here BY HAND. To add a page:
//   1. Create the component in src/pages/ (e.g. src/pages/AboutPage.tsx)
//   2. Import it above (default import from ./pages/), then register
//      its <Route> inside the <Layout> route below — e.g. path="about" → /about
//
// Notes:
//   - `index`     → the "/" home route
//   - `path="*"`  → the 404 catch-all (keep it LAST)
//   - All routes sit inside the <Layout> route, so they share the Layout
//     shell (header/footer slots, analytics, page-level error boundary).
//   - Dynamic params: <Route path="products/:id" element={<ProductDetailPage />} />
// =============================================================================

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        {/* OAuth social-login callback — do NOT link to it (relay return target) */}
        <Route path="auth/social/complete" element={<AuthSocialCompletePage />} />
        <Route path="login" element={<AuthPage />} />
        <Route path="register" element={<AuthPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="w/:username" element={<PublicWishlistPage />} />
        <Route path="w/:username/item/:itemId" element={<WishlistItemPage />} />
        <Route path="w/:username/:boardSlug" element={<PublicWishlistPage />} />
        <Route path="cookie-policy" element={<CookiePolicyPage />} />
        <Route path="community" element={<CommunityPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="email-confermata" element={<EmailConfirmedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
